









































import Vue, {PropType} from 'vue';
import {Component, Prop, Provide} from 'vue-property-decorator'
import axios from 'axios';

import GEForm from '@/Types/GEForm';
import GEFormFields from '@/Types/GEFormFields';
import SchemaField from '@/Types/SchemaField';
import ProcessedUpload from '@/Types/ProcessedUpload';
import FormNavigation from '@/Components/FormNavigation.vue';
import AppLayout from '@/Layouts/AppLayout.vue';
import SchemaService from '@/Services/SchemaService';
import Section from '@/Types/Section';
import FormActions from '@/Components/FormActions.vue';
import PaginationService from '@/Services/PaginationService';
import Page from '@/Types/Page';
import trimErrorKeys from '@/utils/trimErrorKeys';

@Component({
    components: {FormActions, AppLayout, FormNavigation},
})
export default class Form extends Vue {
    @Prop({type: String, required: true}) section!: string
    @Prop({type: Object, required: true}) page!: Page
    @Prop({type: Object as PropType<GEForm>, required: true}) form!: GEForm
    @Prop({type: Object, required: false, default: {}}) errors!: Record<string, string>
    @Prop({type: Array, required: true}) sections!: Section[]

    @Provide() fields: GEFormFields = this.getInitialFields();
    @Provide() destinationSection: string = this.section
    @Provide() destinationPage: null | number = this.page.number
    @Provide() isSubmitting: boolean = false

    // Auto-save functionality
    private autoSaveTimeout: ReturnType<typeof setTimeout> | null = null;
    private readonly AUTO_SAVE_DELAY = 2500; // 2.5 seconds
    private isAutoSaving: boolean = false;
    private lastAutoSaveFields: string = ''; // Track last auto-save to prevent loops
    private isFormReloading: boolean = false; // Track if form is reloading
    private hasUserInteracted: boolean = false; // Track if user has actually interacted with the form
    private readonly stableFormName: string = `questions-${Date.now()}`;

    getInitialFields(): GEFormFields {
        return Array.isArray(this.form.fields) ? {} : {...this.form.fields}
    }

    resetFormFields(): void {
        this.fields = this.getInitialFields();
    }

    get resolvedSection(): any {
        return this.sections.find((section) => section.name === this.section);
    }

    get resolvedSchema(): SchemaField[] {
        const schemaService = new SchemaService(this.page.schema, this.fields);

        return schemaService.process();
    }

    get paginationService(): PaginationService {
        return new PaginationService(this.sections, this.section, this.page);
    }

    get fieldErrors(): {} {
        return trimErrorKeys(this.errors, 'form.fields.');
    }

    get formName(): string {
        return this.stableFormName;
    }

    async handleInput() {
        // Mark that user has interacted with the form
        this.hasUserInteracted = true;

        const sorted: GEFormFields = {...this.fields};
        Object.entries(sorted).forEach(([field, value]) => {
            if (Array.isArray(value)) {
                value.sort()

                sorted[field] = value;
            }
        })

        this.fields = sorted;

        // Auto-save functionality
        this.scheduleAutoSave();
    }

    // Vue lifecycle hook to detect when form data is updated from server
    updated() {
        // Reset auto-save state when form is updated from server
        this.isFormReloading = false;
        this.lastAutoSaveFields = '';
        // Don't reset hasUserInteracted here - keep it true once user has interacted
    }

    async handleSubmit(fields: GEFormFields, force: boolean = false): Promise<void> {
        this.isSubmitting = true;

        interface SubmitData {
            form: {
                id: number,
                section: string,
                page: number,
                destinationSection: string,
                destinationPage?: number,
                fields: GEFormFields,
                autoSave?: boolean,
            }
        }

        try {
            if (force || this.dirty) {
                const data: SubmitData = {
                    form: {
                        id: this.form.id,
                        section: this.section,
                        page: this.page.number,
                        destinationSection: this.destinationSection,
                        fields,
                        autoSave: this.isAutoSaving, // Add auto-save flag
                    },
                };

                if (this.destinationPage) {
                    data.form.destinationPage = this.destinationPage;
                }

                await this.$inertia.post(this.route('form--update'), data, {
                    only: ['sections', 'section', 'page', 'form', 'errors'],
                    preserveState: (page: { props: any }) => {
                        return Object.keys(page.props.errors).length > 0
                    },
                    preserveScroll: this.destinationSection === this.section && this.destinationPage === this.page.number,
                });
            } else {
                await this.$inertia.visit(this.route('form', {
                    section: this.destinationSection,
                    page: this.destinationPage,
                }), {
                    only: ['section', 'page', 'schema', 'title'],
                });
            }
        } finally {
            // Always reset isSubmitting, even if there are errors
            this.isSubmitting = false;
        }
    }

    handleNavigation(destinationSection: string, destinationPage: number): Promise<void> {
        // Cancel any pending auto-save when navigating
        this.cancelAutoSave();

        let changed = false;
        if (this.section !== destinationSection) {
            changed = true;
        }

        if (this.page.number !== destinationPage) {
            changed = true;
        }

        if (changed) {
            if (!this.dirty) {
                this.destinationSection = destinationSection;
                this.destinationPage = destinationPage;

                return this.handleSubmit(this.fields);
            }

            return this.confirmDirtyNavigation(destinationSection, destinationPage)
        }

        return Promise.resolve();
    }

    handleUploadFile({fieldName, file}: { fieldName: string, file: ProcessedUpload }): void {
        const fields = {...this.fields};
        fields[fieldName] = fields[fieldName] || [];
        fields[fieldName].push(file);
        this.fields = fields;

        this.handleSubmit({
            [fieldName]: this.fields[fieldName],
        }, true);
    }

    handleRemoveFile({fieldName, remaining}: { fieldName: string, remaining: string[] }): void {
        const fields = {...this.fields};

        const files = fields[fieldName];
        if (files) {
            fields[fieldName] = files.filter(({url}: ProcessedUpload) => remaining.includes(url));
            this.fields = fields;

            this.handleSubmit({
                [fieldName]: this.fields[fieldName],
            }, true);
        }
    }

    async confirmDirtyNavigation(section: string, page: number) {
        const {isConfirmed, isDenied} = await this.$swal.fire({
            title: 'Save changes?',
            text: 'You\'ve made some changes, do you want to save them?',
            icon: 'question',
            showCancelButton: true,
            showDenyButton: true,
            confirmButtonText: 'Save changes',
            denyButtonText: 'Discard changes',
        })

        if (isConfirmed || isDenied) {
            if (isDenied) {
                this.resetFormFields();
            }

            this.destinationSection = section;
            this.destinationPage = page;

            await this.$formulate.submit(this.formName);
        }
    }

    async handleBack(): Promise<void> {
        // Cancel any pending auto-save when going back
        this.cancelAutoSave();

        const pagination = this.paginationService.getPrev()

        if (pagination) {
            const {section, pageNum} = pagination;

            if (!this.dirty) {
                this.destinationSection = section;
                this.destinationPage = pageNum;

                return this.handleSubmit(this.fields);
            }

            return this.confirmDirtyNavigation(section, pageNum);
        }
    }

    handleSaveStay(): void {
        // For manual saves, use all current page fields to satisfy validation
        const currentPageFields = this.getCurrentPageFields();
        this.handleSubmit(currentPageFields, true);
    }

    handleSaveComplete(): void {
        this.destinationSection = 'overview';
        this.destinationPage = null;
        // For manual saves, use all current page fields to satisfy validation
        const currentPageFields = this.getCurrentPageFields();
        this.handleSubmit(currentPageFields, true);
    }

    handleSaveContinue(): void {
        const pagination = this.paginationService.getNext()
        if (pagination) {
            const {section, pageNum} = pagination;
            this.destinationSection = section;
            this.destinationPage = pageNum;
            // For manual saves, use all current page fields to satisfy validation
            const currentPageFields = this.getCurrentPageFields();
            this.handleSubmit(currentPageFields, true);
        }
    }

    handleFailedValidation(invalid: Record<string, Vue>) {
        const [first] = Object.values(invalid);

        const element = first.$el;

        if (element instanceof HTMLElement) {
            window.scrollTo({
                top: element.offsetTop,
                behavior: 'smooth',
            });
        }
    }

    get dirty(): boolean {
        return JSON.stringify(this.form.fields) !== JSON.stringify(this.fields);
    }

    get saveStatus(): string {
        if (this.isAutoSaving) return 'Saving...';
        if (this.dirty) return 'Unsaved changes';
        return 'All changes saved';
    }

    get saveStatusClass(): string {
        if (this.isAutoSaving) return 'text-blue-600';
        if (this.dirty) return 'text-orange-600';
        return 'text-green-600';
    }

    // Auto-save methods
    private scheduleAutoSave(): void {
        // Clear existing timeout
        if (this.autoSaveTimeout) {
            clearTimeout(this.autoSaveTimeout);
        }

        // Don't auto-save if form is reloading
        if (this.isFormReloading) {
            return;
        }

        // Don't auto-save if user hasn't actually interacted with the form
        if (!this.hasUserInteracted) {
            return;
        }

        // Reset the last auto-save signature when user makes a new change
        this.lastAutoSaveFields = '';

        // Check if auto-save should be enabled for this section
        if (!this.shouldAutoSave()) {
            return;
        }

        // Only auto-save if form is dirty and not already submitting
        // Continue auto-saving even when there are validation errors
        if (this.dirty && !this.isSubmitting && !this.isAutoSaving) {
            this.autoSaveTimeout = setTimeout(() => {
                this.performAutoSave();
            }, this.getAutoSaveDelay());
        }
    }

    private cancelAutoSave(): void {
        if (this.autoSaveTimeout) {
            clearTimeout(this.autoSaveTimeout);
            this.autoSaveTimeout = null;
        }
    }

    private async performAutoSave(): Promise<void> {
        if (!this.dirty || this.isSubmitting || this.isAutoSaving || this.isFormReloading) {
            return;
        }

        // Check if there are any changed fields on the current page
        const hasChangedFieldsOnCurrentPage = this.hasChangedFieldsOnCurrentPage();
        if (!hasChangedFieldsOnCurrentPage) {
            return;
        }

        // Get only the changed fields for the current page (since validation is skipped for auto-save)
        const currentPageFields = this.getChangedCurrentPageFields();

        // Create a signature of the fields being saved to prevent loops
        const fieldsSignature = JSON.stringify(currentPageFields);

        // Prevent auto-save loops by checking if we're trying to save the same fields again
        if (fieldsSignature === this.lastAutoSaveFields) {
            return;
        }

        this.isAutoSaving = true;
        this.lastAutoSaveFields = fieldsSignature;

        try {
            // Build payload identical to handleSubmit, but send via axios to avoid Inertia reload
            const data = {
                form: {
                    id: this.form.id,
                    section: this.section,
                    page: this.page.number,
                    destinationSection: this.section,
                    destinationPage: this.page.number,
                    fields: currentPageFields,
                    autoSave: true,
                },
            };

            await axios.post(this.route('form--update'), data, {
                headers: { 'X-Requested-With': 'XMLHttpRequest' },
                validateStatus: (status: number) => status === 204 || (status >= 200 && status < 300),
            });

            // Merge saved fields into original form snapshot so dirty clears without refetching
            this.form.fields = {
                ...this.form.fields,
                ...currentPageFields,
            };

        } catch (error) {
            console.error('Auto-save failed:', error);
        } finally {
            this.isAutoSaving = false;
        }
    }

    private hasChangedFieldsOnCurrentPage(): boolean {
        const currentPageFieldNames = this.resolvedSchema.map(field => field.name);
        
        // Debug logs removed
        
        // Check if any fields on the current page have changed
        const hasChanges = Object.keys(this.fields).some(fieldName => {
            const isMainField = currentPageFieldNames.includes(fieldName);
            // A details field is one that starts with a main field name + '__' but is NOT a main field itself
            const isDetailsField = !isMainField && currentPageFieldNames.some(mainFieldName => 
                fieldName.startsWith(mainFieldName + '__')
            );
            
            if (isMainField || isDetailsField) {
                const currentValue = this.fields[fieldName];
                const originalValue = this.form.fields[fieldName];
                const hasChanged = JSON.stringify(currentValue) !== JSON.stringify(originalValue);
                
                // Debug log removed
                
                return hasChanged;
            }
            
            return false;
        });
        
        // Debug log removed
        return hasChanges;
    }

    private getChangedCurrentPageFields(): GEFormFields {
        // Get field names from the current page's schema
        const currentPageFieldNames = this.resolvedSchema.map(field => field.name);
        
        // For auto-save, we only need to send changed fields since validation is skipped
        const currentPageFields: GEFormFields = {};
        
        Object.keys(this.fields).forEach(fieldName => {
            // Check if this is a main field from the current page
            const isMainField = currentPageFieldNames.includes(fieldName);
            
            // Check if this is a details field for a current page field
            // A details field is one that starts with a main field name + '__' but is NOT a main field itself
            const isDetailsField = !isMainField && currentPageFieldNames.some(mainFieldName => 
                fieldName.startsWith(mainFieldName + '__')
            );
            
            // Debug logs removed
            
            // Only include fields that are relevant to the current page
            if (isMainField || isDetailsField) {
                // For details fields, check if they should be visible based on the parent field value
                if (isDetailsField) {
                    const parentFieldName = currentPageFieldNames.find(mainFieldName => 
                        fieldName.startsWith(mainFieldName + '__')
                    );
                    
                    if (parentFieldName) {
                        const parentValue = this.fields[parentFieldName];
                        
                        // Only include details fields if the parent field value indicates they should be visible
                        if (!this.shouldDetailsFieldBeVisible(parentFieldName, parentValue)) {
                            return; // Skip this details field
                        }
                    }
                }
                
                // Only include fields that have been modified from their original values
                const currentValue = this.fields[fieldName];
                const originalValue = this.form.fields[fieldName];
                
                // Skip empty details fields to prevent validation issues
                if (isDetailsField && (currentValue === '' || currentValue === null || currentValue === undefined)) {
                    return; // Skip empty details fields
                }
                
                if (JSON.stringify(currentValue) !== JSON.stringify(originalValue)) {
                    currentPageFields[fieldName] = currentValue;
                }
            } else {
            }
        });
        
        // Debug log removed
        return currentPageFields;
    }

    private getCurrentPageFields(): GEFormFields {
        // Get field names from the current page's schema
        const currentPageFieldNames = this.resolvedSchema.map(field => field.name);
        
        
        // For manual saves, we need to include ALL fields on the current page to avoid validation errors
        // The backend validates all fields on the page, not just the ones we send
        const currentPageFields: GEFormFields = {};
        
        // Include all fields that are relevant to the current page
        Object.keys(this.fields).forEach(fieldName => {
            // Check if this is a main field from the current page
            const isMainField = currentPageFieldNames.includes(fieldName);
            
            // Check if this is a details field for a current page field
            // A details field is one that starts with a main field name + '__' but is NOT a main field itself
            const isDetailsField = !isMainField && currentPageFieldNames.some(mainFieldName => 
                fieldName.startsWith(mainFieldName + '__')
            );
            
            
            // Only include fields that are relevant to the current page
            if (isMainField || isDetailsField) {
                // For details fields, check if they should be visible based on the parent field value
                if (isDetailsField) {
                    const parentFieldName = currentPageFieldNames.find(mainFieldName => 
                        fieldName.startsWith(mainFieldName + '__')
                    );
                    
                    if (parentFieldName) {
                        const parentValue = this.fields[parentFieldName];
                        
                        // Only include details fields if the parent field value indicates they should be visible
                        // This prevents validation errors for hidden required fields
                        if (!this.shouldDetailsFieldBeVisible(parentFieldName, parentValue)) {
                            return; // Skip this details field
                        }
                    }
                }
                
                // Include the field with its current value
                const currentValue = this.fields[fieldName];
                
                // Skip empty details fields to prevent validation issues
                if (isDetailsField && (currentValue === '' || currentValue === null || currentValue === undefined)) {
                    return; // Skip empty details fields
                }
                
                currentPageFields[fieldName] = currentValue;
            } else {
            }
        });
        
        return currentPageFields;
    }

    private shouldDetailsFieldBeVisible(parentFieldName: string, parentValue: any): boolean {
        // Check the schema to see if this field has conditional logic
        const schemaField = this.resolvedSchema.find(field => field.name === parentFieldName);
        
        if (!schemaField) {
            return false;
        }
        
        // For yes-no-details-input components, honour the configured showOnYes flag
        if (schemaField.component === 'yes-no-details-input') {
            const showOnYes = schemaField.showOnYes ?? true;
            return showOnYes ? parentValue === 'yes' : parentValue === 'no';
        }
        
        // For other conditional components, you can add more logic here
        // For now, default to true to be safe
        return true;
    }

    // Section-specific configuration methods (commented out for future use)
    private shouldAutoSave(): boolean {
        // Currently auto-save is enabled for all sections
        return true;

        // Future section-specific logic examples:
        // 
        // // Disable auto-save for certain sections
        // if (this.section === 'legal') {
        //     return false; // Maybe legal forms need manual review
        // }
        // 
        // // Disable auto-save for specific pages
        // if (this.section === 'financial' && this.page.number === 5) {
        //     return false; // Maybe the final financial page needs manual review
        // }
        // 
        // return true;
    }

    private getAutoSaveDelay(): number {
        // Currently all sections use 2.5 seconds
        return this.AUTO_SAVE_DELAY;

        // Future section-specific delay examples:
        // 
        // switch (this.section) {
        //     case 'financial':
        //         return 3000; // Longer delay for financial data (3 seconds)
        //     case 'strategy':
        //         return 1500; // Shorter delay for strategy (1.5 seconds)
        //     case 'legal':
        //         return 4000; // Even longer delay for legal forms (4 seconds)
        //     case 'talent':
        //         return 2000; // Standard delay for talent (2 seconds)
        //     case 'digital':
        //         return 2000; // Standard delay for digital (2 seconds)
        //     default:
        //         return this.AUTO_SAVE_DELAY; // Default 2.5 seconds
        // }
    }

    // Component lifecycle cleanup
    beforeDestroy() {
        if (this.autoSaveTimeout) {
            clearTimeout(this.autoSaveTimeout);
        }
    }

}

